import { useMemo } from 'react';
import { isObservable, of } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { useObservableValue } from '../../../hooks/useObservable';
import { EMPTY_ARRAY } from '../../../utils';
import { LoaderTalos, LoaderWrapper } from '../../LoaderTalos';
import { readDrawerOptions } from '../drawerOptionsSource';
import { ENTITY_INTERNAL_ROW_ID } from '../EntityAdminClass';
import type { BaseEntityAdminProps, EntityAdminDrawerOptionsView, EntityAdminRecord } from '../types';
import { hasAnyAddAllowed } from '../utils';
import { CustomDrawer } from './CustomDrawer/CustomDrawer';
import { EntityAdminDrawerProvider } from './EntityAdminDrawerContext';
import {
  InputsAndDropdownsDrawer,
  type BaseEntityAdminDrawerProps,
} from './InputsAndDropdownsDrawer/InputsAndDropdownsDrawer';
import { ModifyJsonDrawer } from './ModifyJsonDrawer';

export interface EntityAdminDrawerProps<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>
  extends
    Omit<BaseEntityAdminDrawerProps<TRecord, TDrawerRecord>, 'drawerOptions'>,
    Pick<
      BaseEntityAdminProps<TRecord, TDrawerRecord>,
      'customDrawerLayout' | 'drawerType' | 'allowAddEntity' | 'allowEditEntity' | 'allowDeleteEntity'
    > {
  addingChildEntity: boolean;
  drawerOptions: EntityAdminDrawerOptionsView<TDrawerRecord> | undefined;
  /** Default values function to use when creating a new entity. */
  newEntityDefault?: () => Partial<TDrawerRecord>;
}

function useResolvedDrawerOptions<TDrawerRecord extends EntityAdminRecord>(
  source: EntityAdminDrawerOptionsView<TDrawerRecord> | undefined
) {
  const source$ = useMemo(() => {
    if (isObservable(source)) {
      return source;
    }
    return of(source ?? EMPTY_ARRAY);
  }, [source]);
  const initialOptions = readDrawerOptions(source) ?? EMPTY_ARRAY;
  const options = useObservableValue(() => source$, [source$], initialOptions);
  return options ?? EMPTY_ARRAY;
}

export const EntityAdminDrawer = <TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>({
  drawerOptions: drawerOptionsSource,
  drawerType = 'InputsAndDropdowns',
  ...props
}: EntityAdminDrawerProps<TRecord, TDrawerRecord>) => {
  const drawerOptions = useResolvedDrawerOptions(drawerOptionsSource);
  const keyId = useMemo(() => {
    // get key for entity being shown so we can properly re-render the drawer when the context changes
    if (props.selectedEntities.length === 0) {
      return 'new-' + uuid();
    }
    // if the context changes about the selection, we need to re-render the drawer
    return props.selectedEntities.map(entity => entity[ENTITY_INTERNAL_ROW_ID]).join('-');
  }, [props.selectedEntities]);

  const isEditing = useMemo(
    () => props.selectedEntities?.length > 0 && !props.addingChildEntity,
    [props.selectedEntities, props.addingChildEntity]
  );

  if (!hasAnyAddAllowed(props.allowAddEntity) && !props.allowEditEntity) {
    // No permissions to add or edit entities using the drawer
    return null;
  }

  if (drawerType !== 'JSON' && drawerOptions.length === 0) {
    // For non-JSON drawers, show a loading state if drawerOptions is empty
    return (
      <EntityAdminDrawerProvider>
        <LoaderWrapper background="backgroundContent">
          <LoaderTalos />
        </LoaderWrapper>
      </EntityAdminDrawerProvider>
    );
  }

  return (
    <EntityAdminDrawerProvider>
      {drawerType === 'JSON' && <ModifyJsonDrawer<TRecord, TDrawerRecord> key={keyId} {...props} />}
      {drawerType === 'InputsAndDropdowns' && drawerOptions != null && (
        <InputsAndDropdownsDrawer<TRecord, TDrawerRecord>
          key={keyId}
          drawerOptions={drawerOptions}
          isEditing={isEditing}
          {...props}
        />
      )}
      {drawerType === 'custom' && drawerOptions != null && props.customDrawerLayout != null && (
        <CustomDrawer<TRecord, TDrawerRecord>
          key={keyId}
          drawerOptions={drawerOptions}
          isEditing={isEditing}
          customDrawerLayout={props.customDrawerLayout}
          {...props}
        />
      )}
    </EntityAdminDrawerProvider>
  );
};
